import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Attachment, UploadedFile, SearchMetadata, ToolCallDisplay } from '../../types/chat';
import { startAnimation, completeAnimation } from '../../utils/animationManager';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import axiosInstance from '../../services/api/axiosConfig';
import DocumentViewer from './DocumentViewer';
import SearchResultsDisplay from './SearchResultsDisplay';
import ToolActivityCard from './ToolActivityCard';
import ImageViewer from './ImageViewer';
import ChatDataChart from '../chart/ChatDataChart';
import { detectDataInText } from '../../utils/dataDetector';
import { preprocessMarkdown } from '../../utils/markdownPreprocess';
import { Copy } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

// ファイルアイコンを取得する関数
const getFileIcon = (mimeType: string): string => {
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return '📊'; // Excel
  } else if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
    return '📽️'; // PowerPoint
  } else if (mimeType.includes('pdf')) {
    return '📄'; // PDF
  } else if (mimeType.includes('word') || mimeType.includes('document')) {
    return '📝'; // Word
  } else if (mimeType.includes('image')) {
    return '🖼️'; // 画像
  } else if (mimeType.includes('video')) {
    return '🎬'; // 動画
  } else {
    return '📎'; // その他
  }
};

// ファイルサービス名を取得する関数
const getServiceName = (service: string): string => {
  switch (service) {
    case 'openai':
      return 'OpenAI';
    case 'claude':
      return 'Claude';
    case 'gemini':
      return 'Gemini';
    default:
      return service;
  }
};

// 内部で使用するメッセージ型
interface ChatMessageType {
  id?: number;  // メッセージID (オプショナル)
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed'; // メッセージステータス
  attachments?: Attachment[];
  uploaded_files?: UploadedFile[]; // アップロード済みファイル情報
  suggestions?: string[]; // AIからの返答サジェスト
  search_metadata?: SearchMetadata | null; // 検索メタデータ
  tool_calls?: ToolCallDisplay[]; // チャットツール実行履歴（カード表示用）
}

// メッセージIDを生成するためのヘルパー関数
const getMessageId = (message: ChatMessageType): number => {
  // roleとcontentの最初の10文字とtimestampからハッシュを生成
  const idString = `${message.role}-${message.content.substring(0, 10)}-${message.timestamp}`;
  let hashCode = 0;
  for (let i = 0; i < idString.length; i++) {
    hashCode = ((hashCode << 5) - hashCode) + idString.charCodeAt(i);
    hashCode |= 0; // 32bit整数に変換
  }
  return Math.abs(hashCode);
};


// 日時のフォーマット
const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
};

interface ChatMessageProps {
  message: ChatMessageType;
  isLatestMessage?: boolean;
  onAnimationComplete?: () => void;
  onRegenerate?: (messageId: number) => void;
  isRegenerating?: boolean;
  onToolAction?: (text: string) => void;
  toolActionsDisabled?: boolean;
  executedProposalCodes?: string[];
}




// アップロードファイル表示コンポーネント
const UploadedFiles: React.FC<{ files: UploadedFile[]; isUser: boolean }> = ({ files, isUser }) => {
  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div className={`mb-2 flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
      {files.map((file, index) => {
        const isImage = file.mime_type?.startsWith('image/');

        // S3のURLがある場合はそれを使用、なければ開発中メッセージを表示
        if (isImage && file.s3_url) {
          return (
            <div key={`${file.file_id}-${index}`} className="mb-2.5">
              <img
                src={file.s3_url}
                alt={file.name}
                className="max-w-[300px] max-h-[300px] rounded-lg block"
                onError={(e) => {
                  console.error('画像の読み込みに失敗:', file.s3_url);
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="text-xs text-gray-500 mt-1">
                {file.name}
              </div>
              <div className="text-[10px] text-gray-400">
                OpenAI File ID: {file.file_id}
              </div>
            </div>
          );
        }

        return (
          <div key={`${file.file_id}-${index}`} className={`flex items-center gap-2 p-2 rounded-lg max-w-[250px] text-sm ${isUser ? 'bg-gray-100' : 'bg-gray-50'} border border-gray-200`}>
            <div className="text-xl flex-shrink-0">
              {getFileIcon(file.mime_type || '')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis text-xs" title={file.name}>
                {file.name}
              </div>
              <div className="text-gray-500 text-[10px] mt-0.5">
                {getServiceName(file.service)}にアップロード済み
              </div>
              {isImage && !file.s3_url && (
                <div className="text-[10px] text-gray-500 mt-2 p-1 bg-yellow-100 rounded">
                  ⚠️ 画像プレビュー機能は開発中です
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 添付ファイルコンポーネント
interface AttachmentComponentProps {
  attachment: Attachment;
  onOpen: (attachment: Attachment) => void;
}

const AttachmentComponent: React.FC<AttachmentComponentProps> = ({ attachment, onOpen }) => {
  const isImage = attachment.type === 'image';

  return (
    <div className="relative rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-md aspect-video w-full transition-transform hover:-translate-y-0.5 hover:shadow-lg">
      {isImage ? (
        <ImageViewer
          attachment={attachment}
          onClick={() => onOpen(attachment)}
        />
      ) : (
        <video
          className="w-full h-full object-cover cursor-pointer"
          controls
          onClick={() => onOpen(attachment)}
        >
          <source src={attachment.s3_url || attachment.url} type={attachment.mime_type} />
          お使いのブラウザは動画の再生に対応していません。
        </video>
      )}
      {!isImage && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[10px] px-1 py-0.5 text-center whitespace-nowrap overflow-hidden text-ellipsis">
          {attachment.filename}
        </div>
      )}
    </div>
  );
};

// PPTダウンロードコンポーネント
const PPTDownloadLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => {
  const { showToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      // download:プロトコルからファイル名を抽出
      const filename = href.replace('download:', '');

      // APIを通じてPPTファイルをダウンロード
      const response = await axiosInstance.get(`/ppt/download/${encodeURIComponent(filename)}`, {
        responseType: 'blob'
      });

      // Blobからダウンロードリンクを作成
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.split('/').pop() || 'presentation.pptx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      showToast('ファイルのダウンロードに失敗しました。', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      className="inline-flex items-center gap-2 px-5 py-3 my-2 bg-red-500 text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-md disabled:bg-red-300 disabled:cursor-not-allowed disabled:transform-none"
      onClick={handleDownload}
      disabled={isDownloading}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 15.5L12 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 11.5L12 15.5L8 11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 16.5V18.5C3 19.6046 3.89543 20.5 5 20.5H19C20.1046 20.5 21 19.6046 21 18.5V16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {isDownloading ? 'ダウンロード中...' : children}
    </button>
  );
};

export const NewChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLatestMessage = false,
  onAnimationComplete,
  onRegenerate,
  isRegenerating = false,
  onToolAction,
  toolActionsDisabled = false,
  executedProposalCodes,
}) => {
  const isUser = message.role === 'user';
  const [displayedText, setDisplayedText] = useState<string>(() => {
    // ユーザーメッセージは即座に全文表示、AIメッセージは空文字から開始
    return isUser ? message.content : '';
  });
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(() => {
    // ユーザーメッセージは最初から完了状態
    return isUser;
  });

  // 添付ファイル関連の状態
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);

  // トースト表示用の状態
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  // アニメーション全体をブロックするフラグ
  const animationBlockRef = useRef<boolean>(false);
  // アニメーション完了通知を送信済みかどうかのフラグ
  const completionNotifiedRef = useRef<boolean>(false);

  // アニメーション開始済みかどうかを記録
  const animationStartedRef = useRef<boolean>(false);
  // アニメーション強制完了フラグ
  const forceCompleteRef = useRef<boolean>(false);
  // SSE ストリーミングで本文を受け取ったメッセージか（タイピングアニメーションを使わない）
  const streamedRef = useRef<boolean>(false);

  // マウント時とisLatestMessageの変更時に初期設定を行う
  useEffect(() => {

    // ユーザーメッセージは処理しない（初期状態で既に設定済み）
    if (isUser) {
      return;
    }

    // AI メッセージの処理
    if (isLatestMessage) {

      // 最新のAIメッセージでアニメーション状態を再設定
      if (!animationStartedRef.current) {
        animationStartedRef.current = true;
        // 確実に空文字から開始
        setDisplayedText('');
        setIsTyping(true);
        setIsDone(false);
        completionNotifiedRef.current = false;
      }
    } else {
      // 最新でないAIメッセージは即時表示
      if (displayedText !== message.content) {
        setDisplayedText(message.content);
        setIsDone(true);
        setIsTyping(false);
      }
    }
  }, [isLatestMessage, isUser, message.content, displayedText]);

  // 最新メッセージフラグが変わった場合のハンドリング
  useEffect(() => {

    // アニメーション中に最新メッセージフラグが変更された場合の処理
    if (!isLatestMessage && isTyping) {
      // アニメーションは継続するが、強制完了フラグをセット
      forceCompleteRef.current = true;
    }
  }, [isLatestMessage]);

  // タイピングアニメーションのロジック
  useEffect(() => {
    // ユーザーメッセージは処理しない
    if (isUser) {
      return;
    }

    // ストリーミング中のメッセージは即座に表示（タイピングアニメーションなし）
    // SSEでリアルタイムに更新されるため、受信した文字をそのまま表示
    if (message.status === 'processing') {
      streamedRef.current = true;
      setDisplayedText(message.content);
      setIsTyping(false);
      return;
    }

    // ストリーミングを終えたメッセージは確定本文をそのまま出す。
    // 最後の delta と complete が同じレンダーで反映されると上の processing 分岐を通らないため、
    // ここで同期しないと表示が最後の delta の直前で止まる。
    if (streamedRef.current && !isTyping) {
      setDisplayedText(prev => (prev === message.content ? prev : message.content));
      return;
    }

    // アニメーションがすでに開始されていて、最新メッセージフラグが変わった場合でも
    // 強制完了フラグがセットされていれば、アニメーションを続行
    if (!isLatestMessage && !forceCompleteRef.current) {
      if (displayedText !== message.content) {
        setDisplayedText(message.content);
        setIsDone(true);
        setIsTyping(false);
      }
      return;
    }

    // タイピングが完了していれば何もしない
    if (isDone || !isTyping) {
      return;
    }


    // アニメーションのためのタイマー参照を保持
    const timersRef: NodeJS.Timeout[] = [];
    let currentIndex = 0;
    const totalLength = message.content.length;

    // 文字を順番に表示する関数
    const typeNextChar = () => {
      if (currentIndex < totalLength) {
        const nextChar = message.content.charAt(currentIndex);


        setDisplayedText(prev => {
          // 完全に新しい文字列を設定（重複防止）
          return message.content.substring(0, currentIndex + 1);
        });
        currentIndex++;

        // 句読点の後は少し長めの遅延を入れる
        const isPunctuation = ['.', '。', '!', '?', '！', '？', '、', ','].includes(nextChar);
        const delay = Math.floor(Math.random() * 10) + (isPunctuation ? 30 : 5);

        const timerId = setTimeout(typeNextChar, delay);
        timersRef.push(timerId);
      } else {
        // タイピング完了前にブロック
        animationBlockRef.current = true;


        // 状態更新
        setDisplayedText(message.content); // 確実に全文字を表示
        setIsTyping(false);
        setIsDone(true);

        // グローバルアニメーション管理に完了を通知
        completeAnimation(getMessageId(message));

        // アニメーション完了を親コンポーネントに通知
        if (onAnimationComplete && (isLatestMessage || forceCompleteRef.current) && !completionNotifiedRef.current) {
          // フラグを立ててダブル通知を防止
          completionNotifiedRef.current = true;

          // レンダリングが完了してから通知
          setTimeout(() => {
            if (onAnimationComplete) {
              onAnimationComplete();
            }
          }, 500);
        }
      }
    };

    // アニメーション開始前にフラグをリセット
    completionNotifiedRef.current = false;

    // グローバルアニメーション管理に開始を通知
    startAnimation(getMessageId(message));

    // アニメーション開始（少し遅延させる）
    const initialTimerId = setTimeout(typeNextChar, 200);
    timersRef.push(initialTimerId);

    // クリーンアップ関数で全てのタイマーをクリア
    return () => {
      timersRef.forEach(timerId => clearTimeout(timerId));
    };
  }, [isTyping, isDone, isUser, isLatestMessage, message.content, message.status, onAnimationComplete]);

  // 添付ファイルを開く
  const handleOpenAttachment = (attachment: Attachment) => {
    setSelectedAttachment(attachment);
  };

  // 添付ファイルを閉じる
  const handleCloseAttachment = () => {
    setSelectedAttachment(null);
  };

  // コピーハンドラー
  const handleCopy = async (isUserMessage: boolean) => {
    try {
      await navigator.clipboard.writeText(message.content);
      const message_text = isUserMessage ? 'コピーしました' : '回答をコピーしました';
      setToastMessage(message_text);
      setShowToast(true);

      // 3秒後に自動的に非表示
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    } catch (error) {
      console.error('クリップボードへのコピーに失敗しました:', error);
    }
  };

  // 添付ファイル情報のデバッグログ出力
  useEffect(() => {
    if (message.attachments && message.attachments.length > 0) {
    }
  }, [message.id, message.attachments, message.content, isUser]);

  // Detect chart data in AI responses (only after streaming is done)
  const chartData = useMemo(() => {
    if (isUser || !isDone || !displayedText) return [];
    return detectDataInText(displayedText);
  }, [isUser, isDone, displayedText]);

  // ユーザーメッセージの表示
  if (isUser) {
    // [document]タグが含まれているかチェック
    const hasDocumentTag = message.content.includes('[document]');

    return (
      <div className="flex items-start space-x-3 justify-end">
        <div className="max-w-3xl order-first">
          {message.timestamp && (
            <div className="flex items-center justify-end space-x-2 mb-2">
              <span className="text-sm text-gray-500">{formatDate(message.timestamp)}</span>
            </div>
          )}

          <div className="rounded-2xl px-4 py-3 bg-gray-50 text-gray-900 ml-12">
            {/* アップロードファイル表示 */}
            <UploadedFiles files={message.uploaded_files || []} isUser={isUser} />

            <div className="text-base leading-relaxed prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-h1:text-3xl prose-h1:font-bold prose-h2:text-2xl prose-h2:font-bold prose-h3:text-lg prose-h3:font-semibold prose-p:text-base prose-li:text-base prose-strong:font-semibold prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:p-3 prose-pre:rounded-lg prose-pre:overflow-x-auto">
              {hasDocumentTag ? (
                // [document]タグが含まれている場合はDocumentViewerを使用
                <DocumentViewer content={message.content} />
              ) : (
                // 通常のMarkdown表示
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="font-bold text-3xl mt-6 mb-4 text-gray-900">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="font-bold text-2xl mt-6 mb-3 text-gray-900">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="font-semibold text-lg mt-4 mb-2 text-gray-900">
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="font-semibold text-base mt-3 mb-2 text-gray-900">
                        {children}
                      </h4>
                    ),
                    h5: ({ children }) => (
                      <h5 className="font-medium text-sm mt-2 mb-1 text-gray-900">
                        {children}
                      </h5>
                    ),
                    h6: ({ children }) => (
                      <h6 className="font-medium text-xs mt-2 mb-1 text-gray-900">
                        {children}
                      </h6>
                    ),
                    p: ({ children }) => (
                      <p className="mb-2 text-gray-900 whitespace-pre-wrap">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="ml-4 mb-2 space-y-1 list-disc">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="ml-4 mb-2 space-y-1 list-decimal">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-gray-900">
                        {children}
                      </li>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-gray-900">
                        {children}
                      </em>
                    ),
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">
                          {children}
                        </code>
                      ) : (
                        <code className={className}>
                          {children}
                        </code>
                      );
                    },
                    pre: ({ children }) => (
                      <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto mb-2">
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 mb-2">
                        {children}
                      </blockquote>
                    ),
                    hr: () => (
                      <hr className="border-gray-300 my-4" />
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto mb-2 max-w-full">
                        <table className="border-collapse border border-gray-300 w-full table-auto text-xs">
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-gray-50">
                        {children}
                      </thead>
                    ),
                    tbody: ({ children }) => (
                      <tbody>
                        {children}
                      </tbody>
                    ),
                    tr: ({ children }) => (
                      <tr className="border-b border-gray-200">
                        {children}
                      </tr>
                    ),
                    th: ({ children }) => (
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-900 border border-gray-300">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="px-2 py-1.5 text-gray-700 border border-gray-300 break-all">
                        {children}
                      </td>
                    ),
                    a: ({ href, children }) => {
                      // PPTダウンロードリンクの処理
                      if (href && href.startsWith('download:')) {
                        return <PPTDownloadLink href={href}>{children}</PPTDownloadLink>;
                      }
                      // 通常のリンク
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {children}
                        </a>
                      );
                    },
                    img: ({ src, alt }) => (
                      <img
                        src={src}
                        alt={alt}
                        className="max-w-full h-auto rounded-lg mb-2"
                      />
                    )
                  }}
                >
                  {preprocessMarkdown(message.content)}
                </ReactMarkdown>
              )}
            </div>

            {/* 添付ファイルがある場合はそれらを表示 */}
            {message.attachments && message.attachments.length > 0 && (
              <div className={`grid gap-4 mt-4 ${message.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {message.attachments.map((attachment, index) => (
                  <AttachmentComponent
                    key={index}
                    attachment={attachment}
                    onOpen={handleOpenAttachment}
                  />
                ))}
              </div>
            )}
          </div>

          {/* コピーボタン */}
          {message.timestamp && (
            <div className="flex items-center justify-end mt-2">
              <button
                className="flex items-center gap-1 h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                onClick={() => handleCopy(true)}
              >
                <Copy className="w-4 h-4" />
                コピー
              </button>
            </div>
          )}
        </div>

        {/* フルスクリーン表示用のオーバーレイ */}
        {selectedAttachment && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 cursor-pointer" onClick={handleCloseAttachment}>
            <div className="max-w-[90%] max-h-[90%]" onClick={(e) => e.stopPropagation()}>
              {selectedAttachment.type === 'image' ? (
                <img className="max-w-full max-h-[90vh] object-contain" src={selectedAttachment.url} alt={selectedAttachment.filename} />
              ) : (
                <video className="max-w-full max-h-[90vh] object-contain" controls autoPlay>
                  <source src={selectedAttachment.url} type={selectedAttachment.mime_type} />
                  お使いのブラウザは動画の再生に対応していません。
                </video>
              )}
            </div>
            <button
              className="absolute top-5 right-5 bg-white bg-opacity-30 text-white border-none rounded-full w-9 h-9 text-lg flex items-center justify-center cursor-pointer hover:bg-opacity-50"
              onClick={handleCloseAttachment}
            >
              ×
            </button>
          </div>
        )}

        {/* トースト表示 */}
        {showToast && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-opacity duration-300">
            {toastMessage}
          </div>
        )}
      </div>
    );
  }

  // [document]タグが含まれているかチェック
  const hasDocumentTag = displayedText.includes('[document]');

  // AIメッセージの表示
  return (
    <div className="flex items-start space-x-3">
      <div className="w-full">
        <div className="rounded-2xl px-4 py-3">
          {/* アップロードファイル表示 */}
          <UploadedFiles files={message.uploaded_files || []} isUser={isUser} />

          {/* チャットツール実行カード（本文の上に表示） */}
          {message.tool_calls && message.tool_calls.length > 0 && (
            <ToolActivityCard
              toolCalls={message.tool_calls}
              onToolAction={onToolAction}
              actionsDisabled={toolActionsDisabled}
              executedProposalCodes={executedProposalCodes}
            />
          )}

          <div className="text-base leading-relaxed prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-h1:text-3xl prose-h1:font-bold prose-h2:text-2xl prose-h2:font-bold prose-h3:text-lg prose-h3:font-semibold prose-p:text-base prose-li:text-base prose-strong:font-semibold prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:p-3 prose-pre:rounded-lg prose-pre:overflow-x-auto">
            {hasDocumentTag ? (
              // [document]タグが含まれている場合はDocumentViewerを使用
              <DocumentViewer content={displayedText} />
            ) : (
              // 通常のMarkdown表示
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({ children }) => (
                    <h1 className="font-bold text-3xl mt-6 mb-4 text-gray-900">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="font-bold text-2xl mt-6 mb-3 text-gray-900">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="font-semibold text-lg mt-4 mb-2 text-gray-900">
                      {children}
                    </h3>
                  ),
                  h4: ({ children }) => (
                    <h4 className="font-semibold text-base mt-3 mb-2 text-gray-900">
                      {children}
                    </h4>
                  ),
                  h5: ({ children }) => (
                    <h5 className="font-medium text-sm mt-2 mb-1 text-gray-900">
                      {children}
                    </h5>
                  ),
                  h6: ({ children }) => (
                    <h6 className="font-medium text-xs mt-2 mb-1 text-gray-900">
                      {children}
                    </h6>
                  ),
                  p: ({ children }) => (
                    <p className="mb-2 text-gray-900 whitespace-pre-wrap">
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="ml-4 mb-2 space-y-1 list-disc">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="ml-4 mb-2 space-y-1 list-decimal">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-gray-900">
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-900">
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-gray-900">
                      {children}
                    </em>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">
                        {children}
                      </code>
                    ) : (
                      <code className={className}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto mb-2">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 mb-2">
                      {children}
                    </blockquote>
                  ),
                  hr: () => (
                    <hr className="border-gray-300 my-4" />
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto mb-2 max-w-full">
                      <table className="border-collapse border border-gray-300 text-xs w-full table-auto [&_th]:text-center [&_td]:align-top [&_td:first-child]:text-center [&_td:first-child]:align-middle">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-gray-50">
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody>
                      {children}
                    </tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="border-b border-gray-200">
                      {children}
                    </tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-2 py-1.5 font-semibold text-gray-900 border border-gray-300 whitespace-nowrap">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-2 py-1.5 text-gray-700 border border-gray-300 break-all">
                      {children}
                    </td>
                  ),
                  a: ({ href, children }) => {
                    // PPTダウンロードリンクの処理
                    if (href && href.startsWith('download:')) {
                      return <PPTDownloadLink href={href}>{children}</PPTDownloadLink>;
                    }
                    // 通常のリンク
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {children}
                      </a>
                    );
                  },
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt}
                      className="max-w-full h-auto rounded-lg mb-2"
                    />
                  )
                }}
              >
                {preprocessMarkdown(displayedText)}
              </ReactMarkdown>
            )}
            {isTyping && !isDone && <span className="inline-block w-2 h-4 bg-blue-500 ml-1 animate-pulse">|</span>}
          </div>

          {/* 添付ファイルがある場合はそれらを表示 */}
          {message.attachments && message.attachments.length > 0 && (() => {
            // デバッグログ: AIメッセージの添付ファイル表示条件
            if (message.role === 'assistant') {
            }
            return isDone;
          })() && (
            <div className={`grid gap-4 mt-4 ${message.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {message.attachments.map((attachment, index) => (
                <AttachmentComponent
                  key={index}
                  attachment={attachment}
                  onOpen={handleOpenAttachment}
                />
              ))}
            </div>
          )}

          {/* 検索結果の表示（AIメッセージのみ、アニメーション完了後） */}
          {!isUser && isDone && message.search_metadata && (
            <SearchResultsDisplay searchMetadata={message.search_metadata} />
          )}

          {/* Chart visualization for detected data in AI responses */}
          {!isUser && isDone && chartData.length > 0 && (
            <div className="mt-4">
              {chartData.map((data, index) => (
                <ChatDataChart key={index} data={data} />
              ))}
            </div>
          )}
        </div>

        {message.timestamp && (
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">{formatDate(message.timestamp)}</span>
            {/* コピー以外のアクションボタンは非表示（旧 ChatPage.scss の挙動を維持） */}
            <div className="flex items-center space-x-1 [&>button:not(:first-child)]:hidden">
              <button
                className="flex items-center gap-1 h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                onClick={() => navigator.clipboard.writeText(message.content)}
              >
                <Copy className="w-4 h-4" />
                回答をコピー
              </button>
              <button
                className="h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  if (message.id && onRegenerate) {
                    onRegenerate(message.id);
                  }
                }}
                disabled={isRegenerating || !message.id || !onRegenerate}
                title="再生成"
              >
                🔄
              </button>
              <button className="h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
                👍
              </button>
              <button className="h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
                👎
              </button>
              <button className="h-8 px-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
                ⋯
              </button>
            </div>
          </div>
        )}
      </div>

      {/* フルスクリーン表示用のオーバーレイ */}
      {selectedAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 cursor-pointer" onClick={handleCloseAttachment}>
          <div className="max-w-[90%] max-h-[90%]" onClick={(e) => e.stopPropagation()}>
            {selectedAttachment.type === 'image' ? (
              <img className="max-w-full max-h-[90vh] object-contain" src={selectedAttachment.url} alt={selectedAttachment.filename} />
            ) : (
              <video className="max-w-full max-h-[90vh] object-contain" controls autoPlay>
                <source src={selectedAttachment.url} type={selectedAttachment.mime_type} />
                お使いのブラウザは動画の再生に対応していません。
              </video>
            )}
          </div>
          <button
            className="absolute top-5 right-5 bg-white bg-opacity-30 text-white border-none rounded-full w-9 h-9 text-lg flex items-center justify-center cursor-pointer hover:bg-opacity-50"
            onClick={handleCloseAttachment}
          >
            ×
          </button>
        </div>
      )}

      {/* トースト表示 */}
      {showToast && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-opacity duration-300">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default NewChatMessage;
